import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formName, setFormName] = useState('');
  const [formRating, setFormRating] = useState('5');
  const [formDescription, setFormDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(buildUrl('/api/reviews'))
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success) setReviews(data.reviews || []);
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormRating('5');
    setFormDescription('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const name = formName.trim();
    const description = formDescription.trim();
    const rating = Number(formRating);
    if (!name) {
      setError('Name is required.');
      return;
    }
    if (!description) {
      setError('Description is required.');
      return;
    }
    if (!Number.isFinite(rating) || rating < 1 || rating > 5) {
      setError('Rating must be between 1 and 5.');
      return;
    }
    setSubmitting(true);
    try {
      const isEditing = Boolean(editingId);
      const url = isEditing ? buildUrl(`/api/reviews/${editingId}`) : buildUrl('/api/reviews');
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, rating }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        if (isEditing) {
          setReviews((prev) => prev.map((r) => (r._id === data.review._id ? data.review : r)));
        } else {
          setReviews((prev) => [data.review, ...prev]);
        }
        resetForm();
        setShowAddForm(false);
      } else {
        setError(data.message || `Failed to ${editingId ? 'update' : 'add'} review (${res.status}).`);
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (rev) => {
    setEditingId(rev._id);
    setFormName(rev.name || '');
    setFormRating(String(rev.rating || '5'));
    setFormDescription(rev.description || '');
    setShowAddForm(true);
    setError('');
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (!id) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      const res = await fetch(buildUrl(`/api/reviews/${id}`), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setReviews((prev) => prev.filter((r) => r._id !== id));
        if (editingId === id) {
          resetForm();
          setShowAddForm(false);
        }
      } else if (!res.ok) {
        setError(data.message || `Failed to delete review (${res.status}).`);
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString();
    } catch (_e) {
      return '';
    }
  };

  return (
    <div className="admin-dashboard">
      <h1 className="admin-dashboard-title">Reviews</h1>

      <div className="admin-dashboard-section">
        <h2 className="admin-section-title">Add review</h2>

        {!showAddForm ? (
          <button
            type="button"
            className="admin-video-add-toggle-btn"
            onClick={() => setShowAddForm(true)}
          >
            <i className="far fa-plus" /> Add review
          </button>
        ) : (
          <div className="admin-video-form-wrap">
            <div className="admin-video-form-header">
              <h3 className="admin-video-form-title">New review</h3>
              <button
                type="button"
                className="admin-video-form-close"
                onClick={() => {
                  setShowAddForm(false);
                  setError('');
                }}
              >
                <i className="far fa-times" />
              </button>
            </div>

            {error && (
              <p className="admin-dashboard-error" role="alert">{error}</p>
            )}

            <form className="admin-video-form" onSubmit={handleSubmit}>
              <div className="admin-video-form-grid">
                <div className="admin-video-form-group">
                  <label htmlFor="review-name">Name</label>
                  <input
                    id="review-name"
                    type="text"
                    className="form-control"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Traveller name"
                  />
                </div>
                <div className="admin-video-form-group">
                  <label htmlFor="review-rating">Rating</label>
                  <select
                    id="review-rating"
                    className="form-control"
                    value={formRating}
                    onChange={(e) => setFormRating(e.target.value)}
                  >
                    <option value="5">5 - Excellent</option>
                    <option value="4">4 - Good</option>
                    <option value="3">3 - Average</option>
                    <option value="2">2 - Poor</option>
                    <option value="1">1 - Terrible</option>
                  </select>
                </div>
              </div>
              <div className="admin-video-form-group">
                <label htmlFor="review-description">Description</label>
                <textarea
                  id="review-description"
                  className="form-control"
                  rows="3"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Write the review description"
                />
              </div>
              <div className="admin-video-form-actions">
                <button
                  type="button"
                  className="admin-video-cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-video-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : editingId ? 'Update review' : 'Save review'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="admin-dashboard-section" style={{ marginTop: '1.25rem' }}>
        <h2 className="admin-section-title">All reviews</h2>
        {loading ? (
          <p className="admin-section-description">Loading reviews…</p>
        ) : reviews.length === 0 ? (
          <p className="admin-section-description">No reviews yet. Add one above.</p>
        ) : (
          <div className="admin-dashboard-cards">
            {reviews.map((rev) => (
              <div key={rev._id} className="admin-card">
                <div className="admin-card-body">
                  <span className="admin-card-label">
                    {rev.name} {rev.rating ? `· ${rev.rating}/5` : ''}
                  </span>
                  <span className="admin-card-value" style={{ fontSize: '0.9rem', fontWeight: 400 }}>
                    {rev.description}
                  </span>
                  {rev.createdAt && (
                    <span className="admin-card-label" style={{ marginTop: '0.25rem' }}>
                      {formatDate(rev.createdAt)}
                    </span>
                  )}
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="admin-video-cancel-btn"
                      onClick={() => handleEditClick(rev)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-video-delete-btn"
                      onClick={() => handleDeleteClick(rev._id)}
                      disabled={deletingId === rev._id}
                    >
                      {deletingId === rev._id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteConfirmId && (
        <div
          className="admin-confirm-overlay"
          onClick={() => setDeleteConfirmId(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-confirm-title"
        >
          <div className="admin-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="admin-confirm-title" className="admin-confirm-title">Delete review?</h3>
            <p className="admin-confirm-text">This cannot be undone.</p>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="admin-confirm-cancel"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-confirm-delete"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReviews;
